import React, { useState, useRef, useEffect } from 'react';
import { Form, Button, Image } from 'react-bootstrap';
import { MdCameraAlt } from 'react-icons/md';
import { FaRegEdit } from "react-icons/fa";
import { useUpdateProfilePictureMutation, useGetUserProfileQuery } from '../../features/users/usersApi';
import { toast } from 'react-toastify';

interface ProfilePictureUploadProps {
  label?: string;
  name: string;
  value?: string;
  onChange: (file: File | null, previewUrl?: string) => void;
  errors?: any;
  className?: string;
  labelClassName?: string;
  containerClass?: string;
  currentImageUrl?: string;
  variant?: "default" | "sidebar";
  initials?: string;
}

const ProfilePictureUpload: React.FC<ProfilePictureUploadProps> = ({
  label,
  name,
  onChange,
  errors,
  labelClassName = '',
  containerClass = '',
  currentImageUrl,
  variant = 'default',
  initials = '',
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentImageUrl || null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [updateProfilePicture, { isLoading }] = useUpdateProfilePictureMutation();
  const { refetch } = useGetUserProfileQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  // Update preview URL when currentImageUrl changes
  useEffect(() => {
    setPreviewUrl(currentImageUrl || null);
  }, [currentImageUrl]);

  const handleFileSelect = async (file: File) => {
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }

      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('File size should be less than 2MB');
        return;
      }

      try {
        // Create FormData
        const formData = new FormData();
        formData.append('picture', file);

        console.log('Uploading profile picture to API...');

        // Upload to API
        const response = await updateProfilePicture(formData).unwrap();

        console.log('Profile picture update response:', response);

        if (response.success) {
          // Check if the response includes the updated picture URL
          let pictureUrl = null;
          if (response.data?.picture) {
            pictureUrl = response.data.picture;
          } else if (response.data?.profile_picture) {
            pictureUrl = response.data.profile_picture;
          } else if (response.data?.image_url) {
            pictureUrl = response.data.image_url;
          }

          if (pictureUrl) {
            // Use the returned URL from the API
            setPreviewUrl(pictureUrl);
            onChange(file, pictureUrl);
          } else {
            // Fallback to local preview URL
            const reader = new FileReader();
            reader.onload = (e) => {
              const url = e.target?.result as string;
              setPreviewUrl(url);
              onChange(file, url);
            };
            reader.readAsDataURL(file);
          }

          // Refetch user profile to get updated picture URL
          console.log('Refetching user profile...');
          await refetch();

          toast.success('Profile picture updated successfully');
        } else {
          toast.error(response.message || 'Failed to update profile picture');
        }
      } catch (error) {
        console.error('Error updating profile picture:', error);
        if (error && typeof error === 'object' && 'status' in error) {
          console.error('Error status:', error.status);

        }
        toast.error('Failed to update profile picture');
      }
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const displayImage = previewUrl || currentImageUrl;
  const isSidebar = variant === 'sidebar';
  const actionLabel = isLoading
    ? 'Uploading…'
    : displayImage
      ? 'Update photo'
      : 'Upload photo';

  return (
    <Form.Group className={containerClass}>
      {label && (
        <Form.Label className={labelClassName}>
          {label}
        </Form.Label>
      )}

      <div
        className={`facebook-profile-picture${
          isSidebar ? ' profile-picture-upload--sidebar' : ''
        }`}
      >
        {displayImage ? (
          <div className="profile-picture-wrapper">
            <Image
              src={displayImage}
              alt="Profile picture"
              className="profile-picture-image"
              roundedCircle
            />
            {isSidebar ? (
              <button
                type="button"
                className="profile-picture-sidebar-overlay"
                onClick={openFileDialog}
                disabled={isLoading}
                aria-label={actionLabel}
              >
                <MdCameraAlt aria-hidden />
              </button>
            ) : (
              <div className="profile-picture-overlay">
                <Button
                  variant="light"
                  size="sm"
                  className="change-photo-btn"
                  onClick={openFileDialog}
                  disabled={isLoading}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyItems: "center" }}>
                    <div><FaRegEdit className="me-1" /></div>
                    <div>{isLoading ? 'Uploading...' : 'Change Picture'}</div>
                  </div>
                </Button>
              </div>
            )}
          </div>
        ) : isSidebar ? (
          <button
            type="button"
            className="profile-picture-sidebar-empty"
            onClick={openFileDialog}
            disabled={isLoading}
            aria-label={actionLabel}
          >
            <span className="profile-picture-sidebar-initials">
              {initials || '?'}
            </span>
          </button>
        ) : (
          <div className="profile-picture-placeholder" onClick={openFileDialog}>
            <div className="placeholder-icon">
              <MdCameraAlt size={32} />
            </div>
            <div className="placeholder-text">
              <span className="add-photo-text">Add Photo</span>
            </div>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileInputChange}
          className="d-none"
        />
      </div>

      {errors && errors[name] && (
        <Form.Control.Feedback type="invalid" className="d-block">
          {errors[name]?.message}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  );
};

export default ProfilePictureUpload;
